import React, { useCallback, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api, getApiErrorMessage } from '../../../api/client';
import { unwrap, unwrapArray } from '../../../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import DispatchOutcomeList, { DispatchLoadNote } from '../../../components/rentals/DispatchOutcomeList';
import type { RentalDispatchCompletion } from '../../../types/rental-dispatch-complete';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import type { RentalPeriodListRow } from '../../../types/rental-period';
import { useSyncStore } from '../../../lib/offline/sync-store';
import { formatInstantTenantDay } from '../../../lib/calendar-date';
import { fromDatetimeLocal, toDatetimeLocal } from '../../../lib/datetime-local';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import SignaturePad from '../../../components/SignaturePad';
import { HaulHoursField, haulHoursBody } from '../../../components/rentals/HaulHoursField';
import { useBillHaulHourly } from '../../../hooks/useBillHaulHourly';
import OrphanedOfflineWork from '../../../components/rentals/OrphanedOfflineWork';
import RentalAssetChecklist from '../../../components/rentals/RentalAssetChecklist';
import { RentalInspectionPhotoField, newInspectionId } from '../../../components/rentals/RentalInspectionPhotos';
import {
  rentalChecklistComplete, rentalOverall, structuredInspectionBody, inspectionPhotoExtras, type RentalAnswers,
} from '../../../lib/rental-inspections';
import type { RentalChecklistResponse } from '../../../types/rental-inspection';
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  WifiOff,
  Package,
  FileWarning,
} from 'lucide-react';

interface AssetReturn {
  returned: boolean;
  damage_description: string;
  hours_reading: string;
  needs_cleaning: boolean;
  needs_service: boolean;
  ready_for_yard: boolean;
}

export default function ReturnConditionCheck() {
  const navigate = useNavigate();
  const { dispatchId } = useParams<{ dispatchId: string }>();
  const { isOnline } = useSyncStore();
  // The off-rent picker reads and writes the TENANT's wall clock
  // (companies.time_zone), never the phone's.
  const timeZone = useTenantTimeZone();

  // Form state
  const [assetReturns, setAssetReturns] = useState<Record<string, AssetReturn>>({});
  /**
   * Each returned asset's post-return checklist (api#861): base list plus its
   * category's family, Pass / Minor / Fail / N/A. The condition is derived by
   * the server; a Fail puts the asset in maintenance and opens the work order
   * itself, so this screen no longer creates one.
   */
  const [answers, setAnswers] = useState<Record<string, RentalAnswers>>({});
  const [checklists, setChecklists] = useState<Record<string, RentalChecklistResponse>>({});
  const onChecklistFor = useCallback(
    (assetId: string) => (cl: RentalChecklistResponse) =>
      setChecklists((prev) => (prev[assetId] === cl ? prev : { ...prev, [assetId]: cl })),
    [],
  );
  // datetime-local wants a wall clock. toISOString() is UTC, which pre-fills
  // six hours ahead in Alberta — and tomorrow's date after 18:00. The wall
  // clock is the tenant's; '' (no zone) leaves the picker empty, not guessed.
  const [offRentDate, setOffRentDate] = useState(() =>
    toDatetimeLocal(new Date().toISOString(), timeZone),
  );
  // The zone is still LOADING on a cold open, so the initializer above sees no
  // zone and leaves the picker empty — and an initializer never runs again, so
  // it stayed empty and Complete Check-In failed on "needs a full date and
  // time". Fill it once the zone arrives, unless something is already there.
  // Keyed on the zone only, so a picker the hand cleared is not refilled.
  React.useEffect(() => {
    if (!timeZone) return;
    setOffRentDate((current) => current || toDatetimeLocal(new Date().toISOString(), timeZone));
  }, [timeZone]);
  /**
   * Each asset's inspection id, minted once so its photos can be uploaded
   * against it before the inspection exists (api#1066). General photos go in
   * `photo_urls`; on a Fail, the damage photos go in `damage_photos`. A damage
   * charge copies both.
   */
  const inspectionIds = useRef<Record<string, string>>({});
  const inspectionIdFor = (assetId: string) => (inspectionIds.current[assetId] ??= newInspectionId());
  const [photos, setPhotos] = useState<Record<string, string[]>>({});
  const [damagePhotos, setDamagePhotos] = useState<Record<string, string[]>>({});
  const [photosBusy, setPhotosBusy] = useState<Record<string, boolean>>({});
  const [operatorName, setOperatorName] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const [haulHours, setHaulHours] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Fetch dispatch details
  const {
    data: dispatch,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['rental-dispatch', dispatchId],
    queryFn: () =>
      api.rentals.dispatch
        .get(dispatchId!)
        .then((r: any) => r.data?.data ?? r.data ?? null),
    enabled: !!dispatchId,
    retry: 1,
  });

  // GET /dispatches/:id sends `rd.*`: `order_id`, never `rental_order_id`,
  // and no `period_id` at all, so the old period close never ran.
  const orderId: string = dispatch?.order_id ?? '';
  // Haul hours (api#1054): only when the company bills hauls by the hour and
  // this dispatch names a truck — no truck, no haul line.
  const { on: billHaulHourly } = useBillHaulHourly();
  const askHaulHours = billHaulHourly && !!dispatch?.truck_id;
  const orderNumber: string = dispatch?.order_number ?? '—';

  /**
   * WHAT CAN COME BACK: the order's OPEN rental periods, one per asset still
   * billing. Each carries the period id the close needs and the asset's name.
   */
  const periodsQuery = useQuery({
    queryKey: ['rental-order-periods', orderId],
    queryFn: () => api.rentals.orders.listPeriods(orderId).then((r) => unwrapArray<RentalPeriodListRow>(r.data)),
    enabled: !!orderId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const periodsFailed = queryLoadFailure(periodsQuery) != null;
  // Open means the API said null; `rp.*` always carries the column.
  const openPeriods = (periodsQuery.data ?? []).filter((p) => p.off_rent_at === null);

  /**
   * The condition each asset went out in, for reference. GET /inspections
   * filters on `order_id` and `asset_id` only (it ignored the old `type` and
   * `limit`), so the newest `pre_delivery` row per asset is picked here.
   * Rows arrive newest first.
   */
  const { data: orderInspections } = useQuery({
    queryKey: ['rental-order-inspections', orderId],
    queryFn: () =>
      api.rentals.inspections
        .list({ order_id: orderId })
        .then((r) => unwrapArray<{ asset_id: string; inspection_type: string; condition: string | null; inspection_date: string | null }>(r.data)),
    enabled: !!orderId,
    retry: 1,
  });

  const blankReturn: AssetReturn = {
    returned: false,
    damage_description: '',
    hours_reading: '',
    needs_cleaning: false,
    needs_service: false,
    ready_for_yard: true,
  };
  const returnOf = (assetId: string): AssetReturn => assetReturns[assetId] ?? blankReturn;
  const returning = openPeriods.filter((p) => returnOf(p.asset_id).returned);

  const updateAssetReturn = (assetId: string, updates: Partial<AssetReturn>) => {
    setAssetReturns((prev) => ({
      ...prev,
      [assetId]: { ...(prev[assetId] ?? blankReturn), ...updates },
    }));
  };

  const isFailed = (assetId: string) => rentalOverall(answers[assetId]) === 'fail';
  const damagedReturns = returning.filter((p) => isFailed(p.asset_id));
  const hasDamaged = damagedReturns.length > 0;
  const allAnswered = returning.every(
    (p) => rentalChecklistComplete(checklists[p.asset_id], answers[p.asset_id]) && rentalOverall(answers[p.asset_id]) !== null,
  );

  // The condition this asset went out in, from its newest pre-delivery inspection.
  const getSentOutCondition = (assetId: string) => {
    const found = (orderInspections ?? []).find(
      (i) => i.asset_id === assetId && i.inspection_type === 'pre_delivery'
    );
    if (!found?.condition) return null;
    // inspection_date is an instant; it is shown as the tenant's day. Absent
    // omits the parenthetical.
    const dateStr = formatInstantTenantDay(found.inspection_date, timeZone, { month: 'short', day: 'numeric' }, 'en-US') ?? '';
    return {
      condition: found.condition,
      date: dateStr,
    };
  };

  /**
   * ONE REQUEST: POST /dispatches/:id/return (api#945). Per returned asset the
   * STRUCTURED post-return inspection (its checklist, the damage description
   * on a Fail, the hours reading, the three flags, the rep's name and
   * signature); the API then closes the asset's open period on this order at
   * `returned_at` and, on a Fail, opens a work order and keeps the asset in
   * maintenance — then marks the dispatch completed, all in one transaction.
   *
   * It used to be inspection, dispatch status and period close as separate
   * requests, reporting "Partly saved" when one failed part-way. A retry sends
   * the same request; if the first landed, the API replays its result.
   */
  const submitMutation = useMutation({
    // Never paused: the default `online` mode parks a mutation while the
    // device looks offline, and the hand sees a spinner instead of the failure.
    networkMode: 'always',
    mutationFn: async (): Promise<RentalDispatchCompletion> => {
      // The picker's wall clock, read in the tenant's zone. A value that cannot
      // be read fails loudly, as `new Date('').toISOString()` used to.
      const offRentInstant = fromDatetimeLocal(offRentDate, timeZone);
      if (!offRentInstant) throw new Error('Off-rent date needs a full date and time.');
      const signedAt = new Date().toISOString();
      const res = await api.rentals.dispatch.return(dispatchId!, {
        returned_at: offRentInstant,
        ...(askHaulHours ? haulHoursBody(haulHours) : {}),
        assets: returning.map((p) => {
          const data = returnOf(p.asset_id);
          const damaged = isFailed(p.asset_id);
          const hours = data.hours_reading.trim() === '' ? NaN : Number(data.hours_reading);
          return structuredInspectionBody(p.asset_id, 'post_return', checklists[p.asset_id], answers[p.asset_id], {
            damage_description: damaged ? data.damage_description.trim() : undefined,
            hours_reading: Number.isFinite(hours) ? hours : undefined,
            checklist: [
              { item: 'Needs cleaning', checked: data.needs_cleaning },
              { item: 'Needs service', checked: data.needs_service },
              { item: 'Ready for yard', checked: data.ready_for_yard },
            ],
            signed_by_name: operatorName.trim(),
            signature_url: signature,
            signed_at: signedAt,
            ...inspectionPhotoExtras(
              inspectionIdFor(p.asset_id),
              photos[p.asset_id] ?? [],
              damaged ? damagePhotos[p.asset_id] ?? [] : [],
            ),
          });
        }),
      });
      const data = unwrap<RentalDispatchCompletion>(res.data);
      if (!data) throw new Error('The return was sent, but the answer could not be read. Check with the office before trying again.');
      return data;
    },
    onSuccess: () => {
      setSubmitted(true);
    },
  });

  /**
   * ALWAYS ATTEMPT THE REQUEST, even when `isOnline` says otherwise.
   *
   * This used to short-circuit on `!isOnline` and write the payload to a
   * localStorage array nothing ever read, then show "Saved Offline — will
   * sync automatically when connected". The work was dropped and the hand was
   * told it was safe.
   *
   * There is no queue to fall back to, so the honest thing is to try and let
   * the failure show. Trusting `isOnline` to refuse pre-emptively would also
   * be wrong in the other direction: that flag is a heuristic over a health
   * poll that has reported false before, and refusing a save that would have
   * succeeded is its own way of losing work.
   */
  const missingDamageNote = damagedReturns.some((p) => !returnOf(p.asset_id).damage_description.trim());

  const handleSubmit = () => {
    if (missingDamageNote) return;
    submitMutation.mutate();
  };

  const canSubmit =
    !!signature &&
    operatorName.trim().length > 0 &&
    returning.length > 0 &&
    allAnswered &&
    !missingDamageNote &&
    // A photo still uploading is not confirmed, and the API refuses the lot for it.
    !returning.some((p) => Object.entries(photosBusy).some(([k, busy]) => busy && k.startsWith(`${p.asset_id}:`)));
  const submitError = submitMutation.error;

  // Full-screen loading
  if (isLoading) {
    return (
      <div className="min-h-full bg-fieldiq-dark flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-fieldiq-accent animate-spin" />
      </div>
    );
  }

  // Full-screen error
  if (isError) {
    return (
      <div className="min-h-full bg-fieldiq-dark flex flex-col items-center justify-center px-6">
        <AlertTriangle className="w-12 h-12 text-amber-400 mb-4" />
        <h2 className="text-lg font-bold text-white mb-2">Unable to Load</h2>
        <p className="text-sm text-fieldiq-muted text-center mb-6">
          Could not load return details. Check your connection and try again.
        </p>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-6 py-3 bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-xl min-h-[48px]"
        >
          <RefreshCw size={18} /> Retry
        </button>
      </div>
    );
  }

  // Success screen
  if (submitted) {
    const outcomes = submitMutation.data?.assets ?? [];
    const count = outcomes.length;
    return (
      <div className="min-h-full bg-fieldiq-dark flex flex-col items-center justify-center px-6">
        <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
          <CheckCircle2 className="w-10 h-10 text-green-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-4">Check-In Complete</h2>

        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 w-full max-w-xs mb-6">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-fieldiq-muted">Order</span>
              <span className="text-sm text-white font-medium">#{orderNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-fieldiq-muted">Off rent</span>
              <span className="text-sm text-white font-medium">
                {count === 1 ? '1 asset' : `${count ?? '—'} assets`}
              </span>
            </div>
          </div>
        </div>
        <DispatchOutcomeList assets={outcomes} />
        <DispatchLoadNote load={submitMutation.data?.load} />

        <button
          onClick={() => navigate('/field/rentals')}
          className="px-8 py-3 bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-xl min-h-[48px]"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-fieldiq-dark pb-8">
      <OrphanedOfflineWork />

      {/* Offline banner */}
      {!isOnline && (
        <div className="bg-amber-500/20 border-b border-amber-500/30 px-4 py-2 text-center">
          <div className="flex items-center justify-center gap-2">
            <WifiOff size={14} className="text-amber-400" />
            <span className="text-xs font-medium text-amber-400">
              Offline — you'll need signal to complete return
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div
        className="bg-fieldiq-dark border-b border-fieldiq-border px-4 py-3"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}
      >
        <div className="max-w-[430px] mx-auto flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-lg bg-fieldiq-card flex items-center justify-center min-h-[48px] min-w-[48px]"
          >
            <ArrowLeft size={20} className="text-white" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-white">Return Check-In</h1>
            <p className="text-xs text-fieldiq-muted">Order #{orderNumber}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[430px] mx-auto px-4 py-4 space-y-4">
        {/* Equipment List */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
          <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider mb-3">
            Equipment Condition
          </h3>
          {periodsFailed ? (
            <LoadFailureNotice error={queryFailureError(periodsQuery)} subject="assets on rent on this order" />
          ) : periodsQuery.isLoading && !!orderId ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-5 h-5 text-fieldiq-muted animate-spin" />
            </div>
          ) : !orderId ? (
            <p className="text-sm text-fieldiq-muted py-2">This dispatch names no order, so there is nothing to return against.</p>
          ) : openPeriods.length === 0 ? (
            <div className="flex flex-col items-center py-4 text-fieldiq-muted">
              <Package size={24} className="mb-2 opacity-40" />
              <p className="text-sm">Nothing on this order is on rent</p>
            </div>
          ) : (
            <div className="space-y-4">
              {openPeriods.map((period) => {
                const assetId = period.asset_id;
                const data = returnOf(assetId);
                const sentOut = getSentOutCondition(assetId);
                const showHours = ['generator', 'gen', 'trailer', 'pump'].some(
                  (t) => (period.category || '').toLowerCase().includes(t)
                );

                return (
                  <div
                    key={period.id}
                    data-testid="return-asset"
                    className="bg-fieldiq-dark border border-fieldiq-border rounded-lg p-3 space-y-3"
                  >
                    {/* Asset header */}
                    <label className="flex items-center gap-3 min-h-[40px] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={data.returned}
                        onChange={() => updateAssetReturn(assetId, { returned: !data.returned })}
                        aria-label={`${period.asset_number} returned`}
                        className="w-5 h-5 accent-green-500"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-white">{period.asset_name}</span>
                        <span className="block text-xs text-fieldiq-muted">{period.asset_number}</span>
                      </span>
                    </label>

                    {/* Sent-out condition reference */}
                    {sentOut && (
                      <div className="bg-fieldiq-card/50 rounded-lg px-3 py-2">
                        <p className="text-[10px] text-fieldiq-muted uppercase tracking-wider mb-0.5">
                          Sent out condition
                        </p>
                        <p className="text-xs text-white capitalize">
                          {sentOut.condition}
                          {sentOut.date && (
                            <span className="text-fieldiq-muted"> ({sentOut.date})</span>
                          )}
                        </p>
                      </div>
                    )}

                    {data.returned && (
                      <>
                        <RentalAssetChecklist
                          compact
                          assetId={assetId}
                          inspectionType="post_return"
                          answers={answers[assetId] ?? {}}
                          onChange={(next) => setAnswers((prev) => ({ ...prev, [assetId]: next }))}
                          onChecklist={onChecklistFor(assetId)}
                        />

                        {/* Damage details */}
                        {isFailed(assetId) && (
                          <textarea
                            value={data.damage_description}
                            onChange={(e) =>
                              updateAssetReturn(assetId, { damage_description: e.target.value })
                            }
                            placeholder="Describe damage (required)"
                            aria-label={`${period.asset_number} damage`}
                            rows={3}
                            className={`w-full bg-fieldiq-card border rounded-lg px-3 py-2.5 text-sm text-white placeholder-fieldiq-muted resize-none ${
                              !data.damage_description.trim()
                                ? 'border-red-500/50'
                                : 'border-fieldiq-border'
                            }`}
                          />
                        )}

                        {isFailed(assetId) && (
                          <RentalInspectionPhotoField
                            inspectionId={inspectionIdFor(assetId)}
                            idPrefix={`return-damage-${assetId}`}
                            label={`${period.asset_number} damage photos`}
                            onChange={(keys) => setDamagePhotos((prev) => ({ ...prev, [assetId]: keys }))}
                            onBusyChange={(busy) => setPhotosBusy((prev) => ({ ...prev, [`${assetId}:damage`]: busy }))}
                          />
                        )}

                        <RentalInspectionPhotoField
                          inspectionId={inspectionIdFor(assetId)}
                          idPrefix={`return-${assetId}`}
                          label={`${period.asset_number} photos`}
                          onChange={(keys) => setPhotos((prev) => ({ ...prev, [assetId]: keys }))}
                          onBusyChange={(busy) => setPhotosBusy((prev) => ({ ...prev, [`${assetId}:photos`]: busy }))}
                        />

                        {/* Hours / reading */}
                        {showHours && (
                          <div>
                            <label className="text-[10px] text-fieldiq-muted uppercase tracking-wider">
                              {(period.category || '').toLowerCase().includes('trailer')
                                ? 'Odometer Reading'
                                : 'Engine Hours'}
                            </label>
                            <input
                              type="number"
                              inputMode="decimal"
                              value={data.hours_reading}
                              onChange={(e) =>
                                updateAssetReturn(assetId, { hours_reading: e.target.value })
                              }
                              className="w-full bg-fieldiq-card border border-fieldiq-border rounded-lg px-3 py-2.5 text-sm text-white placeholder-fieldiq-muted mt-1 min-h-[48px]"
                            />
                          </div>
                        )}

                        {/* Cleaning / service flags */}
                        <div className="flex flex-wrap gap-2">
                          {[
                            { key: 'needs_cleaning' as const, label: 'Needs Cleaning' },
                            { key: 'needs_service' as const, label: 'Needs Service' },
                            { key: 'ready_for_yard' as const, label: 'Ready for Yard' },
                          ].map(({ key, label }) => (
                            <button
                              key={key}
                              type="button"
                              aria-pressed={data[key]}
                              onClick={() => updateAssetReturn(assetId, { [key]: !data[key] })}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border min-h-[40px] transition-colors ${
                                data[key]
                                  ? key === 'ready_for_yard'
                                    ? 'bg-green-500/20 text-green-400 border-green-500/30'
                                    : 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                                  : 'bg-fieldiq-card text-fieldiq-muted border-fieldiq-border'
                              }`}
                            >
                              <div
                                className={`w-4 h-4 rounded-sm border flex items-center justify-center ${
                                  data[key] ? 'bg-current border-current' : 'border-fieldiq-border'
                                }`}
                              >
                                {data[key] && (
                                  <svg className="w-3 h-3 text-fieldiq-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                              {label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Off-Rent Timestamp */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
          <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider mb-2">
            Off-Rent Date/Time
          </h3>
          <input
            type="datetime-local"
            aria-label="Off-rent date and time"
            value={offRentDate}
            onChange={(e) => setOffRentDate(e.target.value)}
            className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-3 text-sm text-white min-h-[48px]"
          />
          <p className="text-xs text-fieldiq-muted mt-1">
            When equipment left the site. Defaults to now.
          </p>
        </div>

        {askHaulHours && <HaulHoursField value={haulHours} onChange={setHaulHours} />}

        {/* Damage Report Preview */}
        {hasDamaged && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <FileWarning size={18} className="text-red-400" />
              <h3 className="text-xs font-bold text-red-400 uppercase tracking-wider">
                Damage Report Preview
              </h3>
            </div>
            <div className="space-y-2">
              {damagedReturns.map((period) => (
                <div key={period.id} className="bg-fieldiq-dark/50 rounded-lg p-3">
                  <p className="text-sm font-semibold text-white">{period.asset_name}</p>
                  <p className="text-xs text-fieldiq-muted mt-1">
                    {returnOf(period.asset_id).damage_description || 'No description provided'}
                  </p>
                  <p className="text-[10px] text-amber-400 mt-1">
                    A repair work order will be created, and the asset moved to Maintenance.
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Operator Signature */}
        <div className="space-y-3">
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
            <h3 className="text-xs font-bold text-fieldiq-muted uppercase tracking-wider mb-3">
              Operator Representative
            </h3>
            <input
              type="text"
              value={operatorName}
              onChange={(e) => setOperatorName(e.target.value)}
              placeholder="Operator rep name"
              className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-3 text-sm text-white placeholder-fieldiq-muted min-h-[48px]"
            />
          </div>
          <SignaturePad
            label="Operator Signature"
            onSave={(dataUrl) => setSignature(dataUrl)}
            onClear={() => setSignature(null)}
            value={signature || undefined}
          />
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || submitMutation.isPending}
          className={`w-full py-4 rounded-xl font-semibold text-base min-h-[56px] transition-colors ${
            canSubmit && !submitMutation.isPending
              ? 'bg-fieldiq-accent text-fieldiq-dark active:bg-fieldiq-accent/90'
              : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
          }`}
        >
          {submitMutation.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          ) : (
            'Complete Check-In'
          )}
        </button>

        {submitMutation.isError && (
          <div role="alert" className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
            <p className="text-sm font-semibold text-red-400 mb-1">Not saved</p>
            <p className="text-xs text-red-300/90">
              {getApiErrorMessage(submitError, submitError instanceof Error ? submitError.message : 'The return could not be saved.')}
            </p>
            <p className="text-xs text-red-300/90 mt-1">
              {isOnline
                  ? 'Try again, and tell the office if it keeps failing.'
                  : "There's no connection, and this screen can't hold the return for later. Keep this page open and press Complete Check-In again once you have signal."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
